import React from 'react';
import { 
  X, 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Package,
  Truck,
  MapPin,
  User,
  AlertTriangle
} from 'lucide-react';

const ShipmentEventsModal = ({ isOpen, onClose, shipment, userRole }) => {
  if (!isOpen || !shipment) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter and sort events based on user role
  const getFilteredEvents = () => {
    if (!shipment.events) return [];
    
    let eventsToShow = shipment.events;
    
    // For Cliente and Notificado roles, only show approved events
    if (userRole === 'Cliente' || userRole === 'Notificado') {
      eventsToShow = shipment.events.filter(event => event.approved === true);
    }
    
    // Sort by date (most recent first)
    return [...eventsToShow].sort((a, b) => {
      const dateA = new Date(a.status_date || a.statusDate);
      const dateB = new Date(b.status_date || b.statusDate);
      return dateB - dateA;
    });
  };

  const sortedEvents = getFilteredEvents();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-primary-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Detalles del Envío</span>
              </h2>
              {(userRole === 'Administrador' || userRole === 'Transportista') && (
                <div>
                  <p className="text-primary-100 mt-0.5 text-sm">
                    Manifiesto: {shipment.manifest}
                  </p>
                  <p className="text-primary-100 mt-0.5 text-sm">
                    Referencia: {shipment.reference}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-primary-100 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-140px)]">
          {/* Shipment Info */}
          <div className="bg-gray-50 rounded-lg p-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Información del Envío</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              <div className="flex items-center space-x-1.5">
                <MapPin className="h-3 w-3 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Origen</p>
                  <p className="text-sm font-medium">{shipment.origin?.originName || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1.5">
                <MapPin className="h-3 w-3 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Destino</p>
                  <p className="text-sm font-medium">{shipment.destination?.destinationName || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1.5">
                <Calendar className="h-3 w-3 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Fecha de Creación</p>
                  <p className="text-sm font-medium">{formatDate(shipment.createdAt)}</p>
                </div>
              </div>
              {shipment.deliveredAt && (
                <div className="flex items-center space-x-1.5">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  <div>
                    <p className="text-xs text-gray-500">Fecha de Entrega</p>
                    <p className="text-sm font-medium">{formatDate(shipment.deliveredAt)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center space-x-1.5">
                <Truck className="h-3 w-3 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Estado</p>
                  <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                    shipment.inTransit
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {shipment.inTransit ? 'En Tránsito' : 'Completado'}
                  </span>
                </div>
              </div>
              {/* Notes field - only for Admin/Transportista */}
              {(userRole === 'Administrador' || userRole === 'Transportista') && shipment.notes && (
                <div className="flex items-start space-x-1.5 md:col-span-2 lg:col-span-3">
                  <Package className="h-3 w-3 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Notas</p>
                    <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{shipment.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Events Timeline */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center space-x-1.5">
              <Clock className="h-3 w-3" />
              <span>Historial de Eventos</span>
              <span className="text-xs text-gray-500 font-normal">
                ({sortedEvents.length} evento{sortedEvents.length !== 1 ? 's' : ''}
                {(userRole === 'Cliente' || userRole === 'Notificado') && ' aprobados'})
              </span>
            </h3>

            {sortedEvents.length === 0 ? (
              <div className="text-center py-4">
                <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {(userRole === 'Cliente' || userRole === 'Notificado') 
                    ? 'No hay eventos aprobados para este envío'
                    : 'No hay eventos registrados para este envío'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {sortedEvents.map((event, index) => (
                  <div
                    key={event.id || index}
                    className="bg-white border border-gray-200 rounded p-2 shadow-sm"
                  >
                    {/* Header with status indicator */}
                    <div className="flex items-center justify-between mb-1">
                      {/* Left side: Status detail with indicator */}
                      <div className="flex items-center space-x-1.5 flex-1">
                        <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                          event.approved 
                            ? 'bg-green-400' 
                            : 'bg-yellow-400'
                        }`}></div>
                        <h4 className="text-sm font-medium text-gray-900 flex-1">
                          {event.status_detail || event.statusDetail}
                        </h4>
                      </div>
                      
                      {/* Right side: Approval status badge */}
                      <div className="flex-shrink-0">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded ${
                          event.approved
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {event.approved ? 'Aprobado' : 'Pendiente'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Footer with dates */}
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-0.5 border-t border-gray-100">
                      <div className="flex items-center space-x-0.5">
                        <Calendar className="h-2.5 w-2.5" />
                        <span className="text-xs">Fecha: {formatDate(event.status_date || event.statusDate)}</span>
                      </div>
                      <div className="flex items-center space-x-0.5">
                        <Clock className="h-2.5 w-2.5" />
                        <span className="text-xs">Creado: {formatDateTime(event.created_at || event.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-3 py-1.5 rounded-md hover:bg-gray-700 transition-colors text-xs"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentEventsModal;